// src/constants/DataSearchConstants.js
// *******************************************************************************
// DATA SEARCH CONSTANTS
// *******************************************************************************
// Centralized constants for dataSearch module - aligned with backend ENUMs.
// These constants ensure type-safe field access and consistent translations.
// *******************************************************************************

// *******************************************************************************
// ENTITY TYPE
// *******************************************************************************
export const ENTITY_TYPE = {
  PERSON: 'person',
  COMPANY: 'company',
};

// *******************************************************************************
// HOLDER TYPE (partner type: person vs company)
// *******************************************************************************
export const HOLDER_TYPE = {
  PERSON: 'person',
  COMPANY: 'company',
};

// Translation keys for display
export const HOLDER_TYPE_LABELS = {
  [HOLDER_TYPE.PERSON]: 'holder_type_person', // PF
  [HOLDER_TYPE.COMPANY]: 'holder_type_company', // PJ
};

// Short labels for badges
export const HOLDER_TYPE_SHORT = {
  [HOLDER_TYPE.PERSON]: 'PF',
  [HOLDER_TYPE.COMPANY]: 'PJ',
};

// *******************************************************************************
// ESTABLISHMENT TYPE (headquarters vs branch)
// *******************************************************************************
export const ESTABLISHMENT_TYPE = {
  HEADQUARTERS: 'headquarters',
  BRANCH: 'branch',
};

export const ESTABLISHMENT_TYPE_LABELS = {
  [ESTABLISHMENT_TYPE.HEADQUARTERS]: 'establishment_headquarters', // Matriz
  [ESTABLISHMENT_TYPE.BRANCH]: 'establishment_branch', // Filial
};

// *******************************************************************************
// COMPANY SIZE
// *******************************************************************************
export const COMPANY_SIZE = {
  MICRO: 'micro',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

export const COMPANY_SIZE_LABELS = {
  [COMPANY_SIZE.MICRO]: 'company_size_micro', // ME/MEI
  [COMPANY_SIZE.SMALL]: 'company_size_small', // EPP
  [COMPANY_SIZE.MEDIUM]: 'company_size_medium', // Médio Porte
  [COMPANY_SIZE.LARGE]: 'company_size_large', // Grande Porte
};

// *******************************************************************************
// REGISTRATION STATUS (cadastral situation)
// *******************************************************************************
export const REGISTRATION_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  IRREGULAR: 'irregular',
  CLOSED: 'closed',
  NULL_REGISTRATION: 'null_registration',
};

export const REGISTRATION_STATUS_LABELS = {
  [REGISTRATION_STATUS.ACTIVE]: 'registration_status_active', // Ativa
  [REGISTRATION_STATUS.SUSPENDED]: 'registration_status_suspended', // Suspensa
  [REGISTRATION_STATUS.IRREGULAR]: 'registration_status_irregular', // Inapta
  [REGISTRATION_STATUS.CLOSED]: 'registration_status_closed', // Baixada
  [REGISTRATION_STATUS.NULL_REGISTRATION]: 'registration_status_null', // Nula
};

// For conditional styling
export const REGISTRATION_STATUS_IS_ACTIVE = status =>
  status === REGISTRATION_STATUS.ACTIVE;

// *******************************************************************************
// HOLDER ROLE (qualification/position)
// *******************************************************************************
export const HOLDER_ROLE = {
  MANAGING_PARTNER: 'managing_partner',
  PARTNER: 'partner',
  ADMINISTRATOR: 'administrator',
  DIRECTOR: 'director',
  PRESIDENT: 'president',
  LEGAL_REPRESENTATIVE: 'legal_representative',
  ATTORNEY: 'attorney',
  TREASURER: 'treasurer',
  SECRETARY: 'secretary',
  BOARD_MEMBER: 'board_member',
  SHAREHOLDER: 'shareholder',
  FOUNDER: 'founder',
  MANAGER: 'manager',
  FOREIGN_ADMINISTRATOR: 'foreign_administrator',
  OTHER: 'other',
};

export const HOLDER_ROLE_LABELS = {
  [HOLDER_ROLE.MANAGING_PARTNER]: 'holder_role_managing_partner', // Sócio-Administrador
  [HOLDER_ROLE.PARTNER]: 'holder_role_partner', // Sócio
  [HOLDER_ROLE.ADMINISTRATOR]: 'holder_role_administrator', // Administrador
  [HOLDER_ROLE.DIRECTOR]: 'holder_role_director', // Diretor
  [HOLDER_ROLE.PRESIDENT]: 'holder_role_president', // Presidente
  [HOLDER_ROLE.LEGAL_REPRESENTATIVE]: 'holder_role_legal_representative', // Representante Legal
  [HOLDER_ROLE.ATTORNEY]: 'holder_role_attorney', // Procurador
  [HOLDER_ROLE.TREASURER]: 'holder_role_treasurer', // Tesoureiro
  [HOLDER_ROLE.SECRETARY]: 'holder_role_secretary', // Secretário
  [HOLDER_ROLE.BOARD_MEMBER]: 'holder_role_board_member', // Conselheiro
  [HOLDER_ROLE.SHAREHOLDER]: 'holder_role_shareholder', // Acionista
  [HOLDER_ROLE.FOUNDER]: 'holder_role_founder', // Fundador
  [HOLDER_ROLE.MANAGER]: 'holder_role_manager', // Gerente
  [HOLDER_ROLE.FOREIGN_ADMINISTRATOR]: 'holder_role_foreign_administrator', // Admin Exterior
  [HOLDER_ROLE.OTHER]: 'holder_role_other', // Outro
};

// *******************************************************************************
// AGE RANGE
// *******************************************************************************
export const AGE_RANGE = {
  UNDER_18: 'under_18',
  FROM_18_TO_30: '18_to_30',
  FROM_31_TO_40: '31_to_40',
  FROM_41_TO_50: '41_to_50',
  FROM_51_TO_60: '51_to_60',
  FROM_61_TO_70: '61_to_70',
  FROM_71_TO_80: '71_to_80',
  OVER_80: 'over_80',
  UNKNOWN: 'unknown',
};

export const AGE_RANGE_LABELS = {
  [AGE_RANGE.UNDER_18]: 'age_range_under_18', // 0-17 anos
  [AGE_RANGE.FROM_18_TO_30]: 'age_range_18_to_30', // 18-30 anos
  [AGE_RANGE.FROM_31_TO_40]: 'age_range_31_to_40', // 31-40 anos
  [AGE_RANGE.FROM_41_TO_50]: 'age_range_41_to_50', // 41-50 anos
  [AGE_RANGE.FROM_51_TO_60]: 'age_range_51_to_60', // 51-60 anos
  [AGE_RANGE.FROM_61_TO_70]: 'age_range_61_to_70', // 61-70 anos
  [AGE_RANGE.FROM_71_TO_80]: 'age_range_71_to_80', // 71-80 anos
  [AGE_RANGE.OVER_80]: 'age_range_over_80', // Acima de 80 anos
  [AGE_RANGE.UNKNOWN]: 'age_range_unknown', // Não informado
};

// *******************************************************************************
// TAX REGIME
// *******************************************************************************
export const TAX_REGIME = {
  SIMPLES_NACIONAL: 'simples_nacional',
  LUCRO_PRESUMIDO: 'lucro_presumido',
  LUCRO_REAL: 'lucro_real',
  MEI: 'mei',
  EXEMPT: 'exempt',
};

export const TAX_REGIME_LABELS = {
  [TAX_REGIME.SIMPLES_NACIONAL]: 'tax_regime_simples_nacional', // Simples Nacional
  [TAX_REGIME.LUCRO_PRESUMIDO]: 'tax_regime_lucro_presumido', // Lucro Presumido
  [TAX_REGIME.LUCRO_REAL]: 'tax_regime_lucro_real', // Lucro Real
  [TAX_REGIME.MEI]: 'tax_regime_mei', // MEI
  [TAX_REGIME.EXEMPT]: 'tax_regime_exempt', // Isento
};

// *******************************************************************************
// SEARCH ENGINE
// *******************************************************************************
export const SEARCH_ENGINE = {
  HOD: 'hod',
  CHOKMAH: 'chokmah',
  METATRON: 'metatron',
};

// *******************************************************************************
// SEARCH TYPE
// *******************************************************************************
export const SEARCH_TYPE = {
  CNPJ: 'cnpj',
  CPF: 'cpf',
  NAME: 'name',
  PHONE: 'phone',
  EMAIL: 'email',
};

// *******************************************************************************
// SYNC STATUS
// *******************************************************************************
export const SYNC_STATUS = {
  PENDING: 'pending',
  SYNCED: 'synced',
  DIVERGENT: 'divergent',
  ORPHAN: 'orphan',
};

// *******************************************************************************
// HELPER FUNCTIONS
// *******************************************************************************

/**
 * Get translated label for a constant value
 * @param {string} value - The ENUM value
 * @param {object} labelsMap - The labels mapping object
 * @param {function} translateFn - Translation function (translateX)
 * @returns {string} Translated label or original value
 */
export function getConstantLabel(value, labelsMap, translateFn) {
  if (!value || !labelsMap) return value || '';
  const labelKey = labelsMap[value];
  return labelKey ? translateFn(labelKey) : value;
}

/**
 * Check if holder type is person
 * @param {string} holderType - The holder type ENUM value
 * @returns {boolean}
 */
export function isHolderTypePerson(holderType) {
  return holderType === HOLDER_TYPE.PERSON;
}

/**
 * Check if registration status indicates company is active
 * @param {string} status - The registration status ENUM value
 * @returns {boolean}
 */
export function isRegistrationActive(status) {
  return status === REGISTRATION_STATUS.ACTIVE;
}

/**
 * Check if establishment is headquarters (matriz)
 * @param {string} establishmentType - The establishment type ENUM value
 * @returns {boolean}
 */
export function isHeadquarters(establishmentType) {
  return establishmentType === ESTABLISHMENT_TYPE.HEADQUARTERS;
}
